import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Loader2, QrCode, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import MitgliedSuche from '@/components/MitgliedSuche';
import LinkVersendet from '@/components/unterschrift/LinkVersendet';
import { useAuth } from '@/contexts/AuthContext';
import { membershipsAPI, unterschriftAPI } from '@/lib/api';

/* Upsell ohne neuen Vertrag im Verkaufsrechner (Betreiber 22.10.2026): Mitglied suchen, Baustein wählen,
   das Mitglied unterschreibt per QR-Code am eigenen Handy (oder am iPad an der Theke). Derselbe Weg wie
   „Upsell ohne neuen Vertrag" in der Mitgliedskarte am Lead: Vorgang `zusatzvereinbarung` der
   Unterschrift-Station, Abschluss über upsell.upsell_abschliessen. Laufzeit und Mandat bleiben. */
const euro = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;
const datum = (iso) => iso ? `${String(iso).slice(8, 10)}.${String(iso).slice(5, 7)}.${String(iso).slice(0, 4)}` : '–';
// Ortszeit statt UTC: kurz nach Mitternacht lieferte toISOString noch den Vortag
const heuteLokal = () => new Date().toLocaleDateString('sv-SE');
const heute = heuteLokal;

export default function UpsellVerkauf() {
  const { user, isAdminPermissions } = useAuth();
  const leitung = user?.role === 'admin' || user?.role === 'manager' || isAdminPermissions;
  const [person, setPerson] = useState(null);
  const [aktiv, setAktiv] = useState(null);           // laufende Mitgliedschaft der gewählten Person
  const [bausteine, setBausteine] = useState([]);
  const [laedt, setLaedt] = useState(false);
  const [hinweis, setHinweis] = useState('');
  const [form, setForm] = useState({ baustein: '', ab: heute(), bemerkung: '', monatsbeitrag_neu: '' });
  const [ergebnis, setErgebnis] = useState(null);
  // QR-Code und Link = EIN Vorgang (26.10.2026): 24 h gültig, läuft im Hintergrund weiter; 4 h vor Ablauf Mail ans Mitglied
  const [link, setLink] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const unterschrieben = useCallback((r) => {
    setLink(null); setErgebnis(r); toast.success(`Upsell abgeschlossen: ${r.zusatzvereinbarung.name}`);
  }, []);

  const waehlen = async (p) => {
    setPerson(p); setAktiv(null); setBausteine([]); setHinweis(''); setErgebnis(null); setLink(null);
    if (!p) return;
    setLaedt(true);
    try {
      const m = (await membershipsAPI.member(p.id)).data;
      if (!m.active) { setHinweis('Keine laufende Mitgliedschaft. Für Nichtmitglieder ist es ein neuer Vertrag.'); setLaedt(false); return; }
      const b = (await membershipsAPI.upsellBausteine(m.active.id)).data.bausteine || [];
      setAktiv(m.active); setBausteine(b);
      setForm({ baustein: b[0]?.key || '', ab: heute(), bemerkung: '', monatsbeitrag_neu: '' });
      if (!b.length) setHinweis('Für diese Mitgliedschaft gibt es keinen passenden Baustein.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      setHinweis(typeof d === 'string' ? d : 'Mitgliedschaft konnte nicht geladen werden.');
    }
    setLaedt(false);
  };
  const neu = () => { setLink(null); waehlen(null); };

  const gewaehlt = bausteine.find(b => b.key === form.baustein);
  const vorher = Number(aktiv?.monthly_price || 0);
  const betragFrei = leitung || form.baustein === 'individuell';   // 26.10.2026: individuelle Vereinbarung auch ohne Leitung
  const frei = betragFrei && form.monatsbeitrag_neu !== '' ? Number(String(form.monatsbeitrag_neu).replace(',', '.')) : null;
  const nachher = frei ?? vorher + Number(gewaehlt?.delta_monat || 0);

  const linkErzeugen = async () => {
    if (!form.baustein) { toast.error('Baustein wählen'); return; }
    if (frei !== null && !(frei >= 0)) { toast.error('Monatsbeitrag prüfen'); return; }
    setLinkBusy(true);
    try { setLink((await unterschriftAPI.versenden({ membership_id: aktiv.id, baustein: form.baustein, ab: form.ab, bemerkung: form.bemerkung, monatsbeitrag_neu: frei })).data); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Link konnte nicht erstellt werden'); }
    setLinkBusy(false);
  };

  if (link) {
    return (
      <Card className="max-w-2xl"><CardContent className="p-5">
        <LinkVersendet vorgang={link} onFertig={neu} onZurueckgezogen={() => setLink(null)} onUnterschrieben={unterschrieben} />
      </CardContent></Card>
    );
  }

  if (ergebnis) {
    const zv = ergebnis.zusatzvereinbarung;
    return (
      <Card className="max-w-2xl" data-testid="upsell-verkauf-ergebnis"><CardContent className="p-5 space-y-3 text-sm">
        <p className="text-base font-bold">{zv.name} gilt ab {datum(zv.ab)}.</p>
        <p>{person?.name}: neuer Beitrag <b>{euro(zv.monatsbeitrag_nachher)}</b> / Monat{zv.einmalbetrag ? `, dazu ${euro(zv.einmalbetrag)} einmalig` : ''}. Laufzeit und SEPA-Mandat bleiben.</p>
        {zv.anteilig && <p className="text-xs text-muted-foreground" data-testid="upsell-anteilig">Die Rechnung für diesen Monat ist schon festgeschrieben: {euro(zv.anteilig.betrag)} für den Rest des Monats kommen auf die nächste Rechnung.</p>}
        <p className={`text-xs rounded-lg px-3 py-2 ${ergebnis.pdf_versand?.status === 'gesendet' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'}`}>
          {ergebnis.pdf_versand?.status === 'gesendet' ? `Zusatzvereinbarung als PDF gesendet an ${ergebnis.pdf_versand.an}.` : `PDF nicht gemailt (${ergebnis.pdf_versand?.grund || ergebnis.pdf_versand?.status || 'keine E-Mail'}). Bitte herunterladen.`}
        </p>
        <div className="flex gap-2 justify-end flex-wrap">
          <a className="inline-flex items-center gap-1 text-sm font-medium text-marke hover:underline" href={membershipsAPI.upsellPdfUrl(ergebnis.membership_id || aktiv?.id, zv.nr)} target="_blank" rel="noreferrer"><FileText className="w-4 h-4" /> PDF öffnen</a>
          <Button size="sm" onClick={neu} data-testid="upsell-verkauf-neu">Nächster Upsell</Button>
        </div>
      </CardContent></Card>
    );
  }

  return (
    <div className="max-w-2xl space-y-4" data-testid="upsell-verkauf">
      <p className="text-sm text-muted-foreground">Zusatzbaustein zur laufenden Mitgliedschaft. Kein neuer Vertrag: Laufzeit, Beginn und SEPA-Mandat bleiben, nur der Beitrag ändert sich. Das Mitglied unterschreibt per QR-Code oder Link am eigenen Handy — 24 Stunden lang, auch wenn ihr hier schon weiterarbeitet.</p>
      <div className="space-y-1.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mitglied</span>
        <MitgliedSuche wert={person} onWahl={waehlen} placeholder="Name oder Mitgliedsnummer…" tid="upsell-verkauf-suche" />
      </div>
      {laedt && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {hinweis && <p className="text-sm rounded-lg bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 px-3 py-2" data-testid="upsell-verkauf-hinweis">{hinweis}</p>}

      {aktiv && bausteine.length > 0 && (
        <Card><CardContent className="p-4 space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">{aktiv.plan_name} · {euro(aktiv.monthly_price)} / Monat</p>
          <div className="space-y-1.5">
            {bausteine.map(b => (
              <label key={b.key} className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer ${form.baustein === b.key ? 'border-marke bg-marke/5' : 'border-border/60'}`}>
                <input type="radio" name="upsell-baustein" className="mt-1 accent-marke" checked={form.baustein === b.key} onChange={() => setForm(f => ({ ...f, baustein: b.key, monatsbeitrag_neu: '' }))} />
                <span className="flex-1 min-w-0"><span className="font-medium">{b.name}</span> <span className="text-muted-foreground">· {b.delta_monat ? `+${euro(b.delta_monat)} / Monat` : b.einmalbetrag ? `${euro(b.einmalbetrag)} einmalig` : 'Betrag frei'}</span>{b.hinweis && <span className="block text-xs text-muted-foreground">{b.hinweis}</span>}</span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="space-y-1 text-xs"><span className="font-semibold uppercase tracking-wider text-muted-foreground flex items-center justify-between gap-2">Gilt ab
                  <button type="button" onClick={() => setForm(f => ({ ...f, ab: heuteLokal() }))} data-testid="upsell-ab-sofort"
                    className={`rounded-full px-2 py-0.5 normal-case tracking-normal font-medium border ${form.ab === heuteLokal() ? 'bg-marke text-white border-marke' : 'border-border text-foreground'}`}>ab sofort</button></span>
                  <Input type="date" value={form.ab} onChange={e => setForm(f => ({ ...f, ab: e.target.value }))} className="h-9" /></label>
            {betragFrei && <label className="space-y-1 text-xs"><span className="font-semibold uppercase tracking-wider text-muted-foreground">{leitung ? 'Monatsbeitrag neu (frei, nur Leitung)' : 'Monatsbeitrag neu'}</span><Input inputMode="decimal" placeholder={nachher.toFixed(2).replace('.', ',')} value={form.monatsbeitrag_neu} onChange={e => setForm(f => ({ ...f, monatsbeitrag_neu: e.target.value }))} className="h-9" /></label>}
          </div>
          <Input value={form.bemerkung} onChange={e => setForm(f => ({ ...f, bemerkung: e.target.value }))} placeholder="Bemerkung (optional)" className="h-9" />
          <p className="rounded-lg bg-muted/40 px-3 py-2">Beitrag: <b>{euro(vorher)}</b> auf <b>{euro(nachher)}</b> / Monat{gewaehlt?.einmalbetrag ? `, dazu ${euro(gewaehlt.einmalbetrag)} einmalig` : ''}.</p>
          <div className="flex justify-end gap-2 flex-wrap">
            <Button onClick={linkErzeugen} disabled={linkBusy || !form.baustein} className="gap-1.5 bg-marke text-white hover:bg-marke-dunkel" data-testid="upsell-verkauf-qr"
              title="QR-Code und Link: 24 Stunden gültig, läuft im Hintergrund weiter; 4 Stunden vor Ablauf bekommt das Mitglied eine E-Mail">
              {linkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />} Abschluss per QR-Code
            </Button>
          </div>
        </CardContent></Card>
      )}
      {!aktiv && !laedt && !hinweis && !person && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Gleicher Weg wie „Upsell ohne neuen Vertrag" am Lead.</p>
      )}
    </div>
  );
}
